'use client';

import { type ComponentType, memo, type ReactNode, useMemo } from 'react';

import { ClipType } from '@air/api/types';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  type IconSize,
  ImageIcon,
  SearchIcon,
  SquarePlayIcon,
  StarIcon,
} from '@air/design-icons';
import { cn } from '@air/utils-tailwind';

import { type BreadcrumbItem, Breadcrumbs } from '~/components/Breadcrumbs';
import { IconButton } from '~/components/IconButton';
import { type GeneralLibraryColor } from '~/components/tree/GeneralLibraryTreeItem';

export type HistoryNavigationProps = {
  canGoBack?: boolean;
  canGoForward?: boolean;
  onBack?: () => void;
  onForward?: () => void;
};

const HistoryNavigation = memo(({ canGoBack, canGoForward, onBack, onForward }: HistoryNavigationProps) => (
  <div className="flex items-center">
    <IconButton aria-label="Go back" className="text-neutral-12" disabled={!canGoBack} onClick={onBack} size="small">
      <ChevronLeftIcon size="sm" />
    </IconButton>
    <IconButton
      aria-label="Go forward"
      className="text-neutral-12"
      disabled={!canGoForward}
      onClick={onForward}
      size="small"
    >
      <ChevronRightIcon size="sm" />
    </IconButton>
  </div>
));

HistoryNavigation.displayName = 'HistoryNavigation';

/** Label with icon for header display (views and assets) */
const HeaderLabel = memo(
  ({
    icon: Icon,
    label,
    truncate,
  }: {
    icon: ComponentType<{ className?: string; size?: IconSize }>;
    label: string;
    truncate?: boolean;
  }) => (
    <div className={cn('flex items-center gap-1.5 font-medium text-12 text-neutral-12', truncate && 'min-w-0')}>
      <Icon className="shrink-0 text-neutral-10" size="sm" />
      <span className={cn(truncate && 'truncate font-normal')}>{label}</span>
    </div>
  ),
);

HeaderLabel.displayName = 'HeaderLabel';

export type ContentHeaderViewType = 'search' | 'favorites' | 'recent' | 'library' | 'board';

export type ContentHeaderProps = {
  /** Optional element rendered before history navigation (e.g., workspace selector) */
  workspaceSelector?: ReactNode;
  /** Current view type */
  viewType: ContentHeaderViewType;
  /** Library name (when viewType is library or board) */
  libraryName?: string;
  /** Library icon emoji (when viewType is library or board) */
  libraryIcon?: string;
  /** Library icon color (when viewType is library or board) */
  libraryColor?: GeneralLibraryColor;
  /** Library ID for breadcrumb click handler */
  libraryId?: string;
  /** Current board name (when viewType is board) */
  currentBoardName?: string;
  /** Current board ID for breadcrumb click handler */
  currentBoardId?: string;
  /** Ancestor boards for breadcrumb navigation */
  ancestors?: BreadcrumbItem[];
  /** Handler for library click in breadcrumbs */
  onLibraryClick?: (libraryId: string) => void;
  /** Handler for board click in breadcrumbs */
  onBoardClick?: (boardId: string) => void;
  /** Handler for navigating back */
  onNavigateBack?: () => void;
  /** Handler for navigating forward */
  onNavigateForward?: () => void;
  /** Whether back navigation is available */
  canGoBack?: boolean;
  /** Whether forward navigation is available */
  canGoForward?: boolean;
  /** Right side content (e.g., "Open in Air" button) */
  actions?: ReactNode;
  /** Show loading skeleton for breadcrumbs */
  isLoading?: boolean;
  /** Asset name to display (when viewing asset detail) */
  assetName?: string;
  /** Asset type for icon display */
  assetType?: ClipType;
};

export const ContentHeader = memo(
  ({
    workspaceSelector,
    viewType,
    libraryName,
    libraryIcon,
    libraryColor,
    libraryId,
    currentBoardName,
    currentBoardId,
    ancestors,
    onLibraryClick,
    onBoardClick,
    onNavigateBack,
    onNavigateForward,
    canGoBack,
    canGoForward,
    actions,
    isLoading,
    assetName,
    assetType,
  }: ContentHeaderProps) => {
    const content = useMemo(() => {
      switch (viewType) {
        case 'search':
          return assetName ? (
            <HeaderLabel icon={assetType === ClipType.video ? SquarePlayIcon : ImageIcon} label={assetName} truncate />
          ) : (
            <HeaderLabel icon={SearchIcon} label="Search" />
          );
        case 'favorites':
          return assetName ? (
            <HeaderLabel icon={assetType === ClipType.video ? SquarePlayIcon : ImageIcon} label={assetName} truncate />
          ) : (
            <HeaderLabel icon={StarIcon} label="My favorites" />
          );
        case 'recent':
          return assetName ? (
            <HeaderLabel icon={assetType === ClipType.video ? SquarePlayIcon : ImageIcon} label={assetName} truncate />
          ) : (
            <HeaderLabel icon={ClockIcon} label="Recently added" />
          );
        case 'library':
        case 'board':
          return (
            <Breadcrumbs
              ancestors={ancestors}
              assetName={assetName}
              assetType={assetType}
              currentBoardId={viewType === 'board' ? currentBoardId : undefined}
              currentBoardName={viewType === 'board' ? currentBoardName : undefined}
              isLoading={isLoading}
              libraryColor={libraryColor}
              libraryIcon={libraryIcon}
              libraryId={libraryId}
              libraryName={libraryName}
              onBoardClick={onBoardClick}
              onLibraryClick={onLibraryClick}
            />
          );
        default:
          return null;
      }
    }, [
      ancestors,
      assetName,
      assetType,
      currentBoardId,
      currentBoardName,
      isLoading,
      libraryColor,
      libraryIcon,
      libraryId,
      libraryName,
      onBoardClick,
      onLibraryClick,
      viewType,
    ]);

    return (
      <div className="flex shrink-0 items-center justify-between gap-2 border-neutral-4 border-b px-3 py-2">
        <div className="flex min-w-0 items-center gap-2">
          {workspaceSelector && (
            <>
              <div className="shrink-0">{workspaceSelector}</div>
              <div className="h-4 w-px shrink-0 bg-neutral-6" />
            </>
          )}
          <HistoryNavigation
            canGoBack={canGoBack}
            canGoForward={canGoForward}
            onBack={onNavigateBack}
            onForward={onNavigateForward}
          />
          <div className="flex min-w-0 flex-1">{content}</div>
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
    );
  },
);

ContentHeader.displayName = 'ContentHeader';
